import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./Header.css";
import { AuthContext } from "../AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTCDropdown, setShowTCDropdown] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, userInfo, logout } = useContext(AuthContext);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu when navigating to a new page
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const fetchCategories = () => {
    if (!categoriesLoaded) {
      fetch(`${API_BASE_URL}/api/categories`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch categories: ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          // Filter to only show top-level categories (those without parentCategoryId)
          const mainCategories = data.filter(category => !category.parentCategoryId);
          setCategories(mainCategories);
          setCategoriesLoaded(true);
        })
        .catch(error => {
          // Silent fail (previously only 1 console.error line in this catch block, no other actions)
        });
    }
    setShowCategoryDropdown(true);
    setShowTCDropdown(false);
  };

  const hideCategoryDropdown = () => {
    setShowCategoryDropdown(false);
  };

  const hideTCDropdown = () => {
    setShowTCDropdown(false);
  };

  const showTCItems = () => {
    setShowTCDropdown(true);
    setShowCategoryDropdown(false);
  };

  // Toggle mobile menu
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Close mobile menu
  const closeMenu = () => {
    setMenuOpen(false);
    setShowCategoryDropdown(false);
    setShowTCDropdown(false);
  };

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-item-with-dropdown')) {
        setShowCategoryDropdown(false);
        setShowTCDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Search bar
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm("");
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getProfileLinkPath = () => {
    if (isLoggedIn && userInfo && ["admin", "warehouse", "sales"].includes(userInfo.role)) {
      return "/login";
    }
    return isLoggedIn ? "/dashboard" : "/login";
  };

  const getProfileLinkText = () => {
    if (isLoggedIn && userInfo && ["admin", "warehouse", "sales"].includes(userInfo.role)) {
      return "Login";
    }
    return isLoggedIn ? "My Profile" : "Login";
  };

  const getProfileIconTitle = () => {
    if (isLoggedIn && userInfo && ["admin", "warehouse", "sales"].includes(userInfo.role)) {
      return "Login / Register";
    }
    return isLoggedIn ? "My Profile" : "Login / Register";
  };

  const handleProfileIconClick = (e) => {
    if (isLoggedIn && userInfo &&
      ["admin", "warehouse", "sales"].includes(userInfo.role)) {
      e.preventDefault(); // Prevent default navigation
      logout().then(() => {
        navigate("/login");
      });
    }
  };

  return (
    <header className={scrolled ? "scrolled" : ""}>
      <div className="container">
        {/* Mobile Menu Toggle Button */}
        <div
          className="menu-icon"
          onClick={toggleMenu}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
        </div>
        <div className="company-logo">
          <Link to="/" onClick={closeMenu}>
            <img
              src="./company-logo.png"
              alt="Ardency Logo"
            />
          </Link>
        </div>

        {/* Always-visible search field for phone layouts */}
        <form
          className="mobile-search-form"
          role="search"
          onSubmit={handleSearchSubmit}
        >
          <input
            type="search"
            className="mobile-search-input"
            aria-label="Search products"
            placeholder="Search product"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button
            type="submit"
            className="mobile-search-button"
            aria-label="Search"
          >
            <i className="fas fa-search" aria-hidden="true"></i>
          </button>
        </form>

        {/* Navigation Bar Section */}
        <nav className={`navMenu ${menuOpen ? "open" : ""}`}>
          <Link
            to="/"
            className={isActive("/") ? "active" : ""}
            onClick={closeMenu}
          >
            Home
          </Link>

          <Link
            to="/about"
            className={isActive("/about") ? "active" : ""}
            onClick={closeMenu}
          >
            About Us
          </Link>

          <Link
            to="/category"
            className={isActive("/category") ? "active" : ""}
            onClick={closeMenu}
            onMouseEnter={fetchCategories}
          >
            Products
          </Link>
          {/* Sits immediately after the Products link so the dropdown anchors under it,
              not under the next nav item */}
          <div className="nav-item-with-dropdown">
            {showCategoryDropdown && (
              <div
                className="header-category-dropdown"
                onMouseEnter={() => setShowCategoryDropdown(true)}
                onMouseLeave={hideCategoryDropdown}
              >
                <div className="header-category-dropdown-container">
                  <ul className="header-category-dropdown-list">
                    {categories.map((category) => (
                      <li key={category.categoryID} className="header-category-dropdown-item">
                        <Link
                          to={`/category/${category.categoryID}`}
                          className="header-category-dropdown-link"
                          onClick={closeMenu}
                        >
                          {category.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <Link
            to="/promotion"
            className={isActive("/promotion") ? "active" : ""}
            onClick={closeMenu}
          >
            Promotion
          </Link>

          <Link
            to="/catalogue"
            className={isActive("/catalogue") ? "active" : ""}
            onClick={closeMenu}
          >
            Catalogue
          </Link>

          <Link
            to="/terms"
            className={isActive("/terms") ? "active" : ""}
            onClick={closeMenu}
            onMouseEnter={showTCItems}
          >
            T&amp;C
          </Link>
          <div className="nav-item-with-dropdown">
            {showTCDropdown && (
              <div
                className="header-tc-dropdown"
                onMouseEnter={() => setShowTCDropdown(true)}
                onMouseLeave={hideTCDropdown}
              >
                <div className="header-tc-dropdown-container header-dropdown-container">
                  <ul className="header-tc-dropdown-list">
                    <li className="header-tc-dropdown-item">
                      <Link
                        to="/terms?tab=terms"
                        className="header-tc-dropdown-link"
                        onClick={closeMenu}
                      >
                        TERMS AND CONDITIONS
                      </Link>
                    </li>
                    <li className="header-tc-dropdown-item">
                      <Link
                        to="/terms?tab=return"
                        className="header-tc-dropdown-link"
                        onClick={closeMenu}
                      >
                        RETURN AND REFUND POLICY
                      </Link>
                    </li>
                    <li className="header-tc-dropdown-item">
                      <Link
                        to="/terms?tab=delivery"
                        className="header-tc-dropdown-link"
                        onClick={closeMenu}
                      >
                        SHIPPING & DELIVERY
                      </Link>
                    </li>
                    <li className="header-tc-dropdown-item">
                      <Link
                        to="/terms?tab=privacy"
                        className="header-tc-dropdown-link"
                        onClick={closeMenu}
                      >
                        PRIVACY POLICY
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <Link
            to="/review"
            className={isActive("/review") ? "active" : ""}
            onClick={closeMenu}
          >
            Reviews
          </Link>
          <Link
            to="/contact"
            className={isActive("/contact") ? "active" : ""}
            onClick={closeMenu}
          >
            Contact Us
          </Link>
          <div className="dot"></div>

          {/* Only Mobile Navigation Bar  */}
          <div className="mobile-menu">
            <Link
              to={getProfileLinkPath()}
              className={isActive("/dashboard") || isActive("/login") ? "active" : ""}
              onClick={(e) => {
                closeMenu();
                handleProfileIconClick(e);
              }}
            >
              {getProfileLinkText()}
            </Link>
            <Link
              to="/cart"
              className={isActive("/cart") ? "active" : ""}
              onClick={closeMenu}
            >
              Shopping Cart
            </Link>
          </div>
        </nav>

        {/* Search Field Section */}
        <div className="header-right">
          <div className="box">
            <form name="search" onSubmit={handleSearchSubmit}>
              <input
                type="text"
                className="input"
                name="txt"
                placeholder="Search product"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </form>
            <i className="fas fa-search" onClick={handleSearchSubmit}></i>
          </div>

          {/* Only Desktop Navigation Bar */}
          <div className="icons">
            <Link to="/cart" data-tooltip="Shopping cart">
              <i className="fa fa-shopping-cart" aria-hidden="true"></i>
            </Link>
            <Link
              to={getProfileLinkPath()}
              data-tooltip={isLoggedIn ? "My Profile" : "Login or Register"}
              onClick={handleProfileIconClick}
            >
              <i className="fa fa-user" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
